import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Key, Save, Radio, Info, Shield, Eye, EyeOff, Users, Copy, Check,
  ChevronDown, Bell,
} from 'lucide-react';
import { api, getPosNmiApiBase } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SearchableDropdown from '../SearchableDropdown';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  tenantId: string;
  locationId: string;
  locationName?: string;
  onSaved?: () => void;
}

/**
 * NMI credentials card — one record per (tenantId, locationId).
 *
 * Design decisions reflected here:
 *   - NMI auth is key-only (no merchantId). The security key encodes the
 *     merchant; we don't collect a separate id. We learn the NMI-side merchant
 *     id later from webhook payloads (event_body.merchant.id).
 *   - Collapsible — once credentials are saved, operators spend most of their
 *     time on device management below, so we collapse by default.
 *   - "Subscribe to everything now" — we recommend ticking ALL NMI events on
 *     the endpoint so settlement + chargeback + future subscription features
 *     arrive automatically without re-visiting each tenant's NMI portal.
 */

const PROVIDER_OPTIONS = [
  { value: 'nmi', label: 'NMI Customer-Present Cloud' },
  { value: 'external', label: 'External terminal (manual entry)' },
];

const ENV_OPTIONS = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];

// Full NMI event catalogue. Every group below is safe to subscribe today —
// even subscription + chargeback events flow through to our handler and get
// persisted (chargebacks/settlements) or logged (tokens/account updater) so
// when we ship those features the data is already arriving.
const EVENT_GROUPS = [
  {
    title: 'Card-present sales',
    note: 'Powers POS sale → payment device → receipt + sales pipeline.',
    events: ['transaction.sale.success', 'transaction.sale.failure', 'transaction.sale.unknown'],
    live: true,
  },
  {
    title: 'Voids + refunds',
    note: 'Powers the Void / Refund actions on the transaction detail page.',
    events: [
      'transaction.void.success', 'transaction.void.failure', 'transaction.void.unknown',
      'transaction.refund.success', 'transaction.refund.failure', 'transaction.refund.unknown',
    ],
    live: true,
  },
  {
    title: 'Auth + capture',
    note: 'Pre-auth flow for tip-on-receipt and delayed capture.',
    events: [
      'transaction.auth.success', 'transaction.auth.failure', 'transaction.auth.unknown',
      'transaction.capture.success', 'transaction.capture.failure', 'transaction.capture.unknown',
    ],
    live: true,
  },
  {
    title: 'Credits + validation',
    note: 'Standalone credits and zero-dollar card validation.',
    events: [
      'transaction.credit.success', 'transaction.credit.failure', 'transaction.credit.unknown',
      'transaction.validate.success', 'transaction.validate.failure', 'transaction.validate.unknown',
    ],
    live: true,
  },
  {
    title: 'ACH / check transactions',
    note: 'eCheck settlement notifications for tenants that accept ACH.',
    events: [
      'transaction.check.status.return',
      'transaction.check.status.latereturn',
      'transaction.check.status.settle',
    ],
    live: true,
  },
  {
    title: 'Settlement',
    note: 'Marks transactions as settled + flags batch-close failures for reconciliation reports.',
    events: ['settlement.batch.complete', 'settlement.batch.failure'],
    live: true,
  },
  {
    title: 'Chargebacks',
    note: 'Flags disputed payments + notifies tenant admin + super admin in real time.',
    events: ['chargeback.batch.complete'],
    live: true,
  },
  {
    title: 'Subscriptions (arrives now, used when Subscriptions package ships)',
    note: 'Plan + subscription lifecycle for recurring billing. Persisted + logged so subscription service can consume later.',
    events: [
      'recurring.plan.add', 'recurring.plan.update', 'recurring.plan.delete',
      'recurring.subscription.add', 'recurring.subscription.update', 'recurring.subscription.delete',
    ],
    live: false,
  },
  {
    title: 'Account updater + network tokens',
    note: 'Keeps card-on-file data fresh for returning customers. Persisted for future vault sync.',
    events: [
      'acu.summary.automaticallyupdated',
      'acu.summary.contactcustomer',
      'acu.summary.closedaccount',
      'networktoken.token.create',
      'networktoken.token.refresh',
    ],
    live: false,
  },
];

function webhookUrl(tenantId: string, locationId: string): string {
  return `${getPosNmiApiBase()}/webhooks/nmi/${tenantId}/${locationId}`;
}

const NmiCredentialsCard: React.FC<Props> = ({ tenantId, locationId, locationName, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  const [configured, setConfigured] = useState(false);
  const [provider, setProvider] = useState<'nmi' | 'external'>('nmi');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [securityKey, setSecurityKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [keyMasked, setKeyMasked] = useState(false);
  const [secretMasked, setSecretMasked] = useState(false);
  const [lastTestedAt, setLastTestedAt] = useState<number | null>(null);
  const [lastTestResult, setLastTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Collapsibles — expanded on first visit (before configured), collapsed once saved
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [eventsOpen, setEventsOpen] = useState(false);

  const url = useMemo(() => webhookUrl(tenantId, locationId), [tenantId, locationId]);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const gw = await api.posNmi.getPaymentGateway(tenantId, locationId);
      const isConfigured = !!gw?.configured;
      setConfigured(isConfigured);
      setProvider((gw?.provider || 'nmi') as any);
      setEnvironment((gw?.environment || 'sandbox') as any);
      setSecurityKey('');
      setWebhookSecret('');
      setKeyMasked(!!gw?.securityKeyEncryptedAt);
      setSecretMasked(isConfigured);
      setLastTestedAt(gw?.lastTestedAt || null);
      // Once configured, collapse the details panel to free space for
      // the device section below. First-time visitors see the form open.
      setDetailsOpen(!isConfigured);
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to load gateway config');
    } finally {
      setLoading(false);
    }
  }, [tenantId, locationId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const body: Record<string, unknown> = { provider, environment };
      if (securityKey.trim()) body.securityKey = securityKey.trim();
      if (webhookSecret.trim()) body.webhookSecret = webhookSecret.trim();
      await api.posNmi.putPaymentGateway(tenantId, locationId, body);
      toastService.success('Location credentials saved');
      setSecurityKey('');
      setWebhookSecret('');
      onSaved?.();
      await fetchConfig();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setLastTestResult(null);
      const res = await api.posNmi.testPaymentGateway(tenantId, locationId);
      if (res?.reachable) {
        const countLabel = typeof res.deviceCount === 'number'
          ? ` · ${res.deviceCount} device${res.deviceCount === 1 ? '' : 's'} on file`
          : '';
        setLastTestResult({ ok: true, message: `NMI reachable · ${res.latencyMs || 0}ms${countLabel}` });
        toastService.success('NMI reachable');
      } else {
        // Build the most specific diagnostic we can — NMI body message first,
        // then our error code (network / timeout / etc.), then HTTP status.
        const reason =
          res?.message ||
          res?.error ||
          res?.body?.message ||
          res?.body?.error ||
          (res?.status ? `NMI returned HTTP ${res.status}` : 'No response from NMI');
        setLastTestResult({ ok: false, message: `NMI unreachable — ${reason}` });
        toastService.error('NMI unreachable');
      }
      await fetchConfig();
    } catch (err: any) {
      setLastTestResult({ ok: false, message: err?.message || 'Test failed' });
      toastService.error(err?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be blocked */ }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60">
        <RubyLoader variant="section" />
      </div>
    );
  }

  // Shared input classes — explicit text/bg so themes can't hide text
  const inputCls = 'w-full px-3 py-2 text-sm font-mono text-stone-900 bg-white border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none placeholder-stone-400';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
      {/* Collapsible header — always visible so operators can tell the card
          apart from device details below. */}
      <button
        type="button"
        onClick={() => setDetailsOpen(v => !v)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-stone-50 transition-colors"
      >
        <div className="w-9 h-9 rounded-lg bg-stone-900 flex items-center justify-center shrink-0">
          <Key size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-stone-800 font-semibold text-sm">NMI Credentials</h3>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200">
              <Users size={10} />
              Location-wide{locationName ? ` · ${locationName}` : ''}
            </span>
          </div>
          <p className="text-stone-500 text-xs mt-0.5">
            {configured
              ? 'Applies to every POS terminal at this location. Click to edit.'
              : 'Save credentials before pairing a payment device or processing card payments.'}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border shrink-0 ${
          configured ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${configured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {configured ? 'Configured' : 'Not configured'}
        </span>
        <ChevronDown size={16} className={`text-stone-400 shrink-0 transition-transform ${detailsOpen ? '' : '-rotate-90'}`} />
      </button>

      {detailsOpen && (
        <div className="px-5 pb-5 border-t border-stone-100 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">Provider</label>
              <SearchableDropdown
                options={PROVIDER_OPTIONS}
                value={provider}
                onChange={(v) => setProvider((v as any) || 'nmi')}
                placeholder="Select provider"
                compact
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">Environment</label>
              <SearchableDropdown
                options={ENV_OPTIONS}
                value={environment}
                onChange={(v) => setEnvironment((v as any) || 'sandbox')}
                placeholder="Select environment"
                compact
              />
            </div>
          </div>

          {/* Prominent saved-state banner when keys already exist. Makes it
              unambiguous that the empty inputs below are "password-style
              blanks" and not "nothing is saved". */}
          {(keyMasked || secretMasked) && (
            <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <Check size={14} className="text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-emerald-800">Credentials stored securely</p>
                <p className="text-[11px] text-emerald-700 mt-0.5">
                  Inputs below are blank by design — we never display saved secrets. Leave a field empty to keep its stored value; type a new value to replace it.
                </p>
              </div>
            </div>
          )}

          <div className="mb-3">
            <label className="block text-xs font-medium text-stone-700 mb-1 inline-flex items-center gap-1 flex-wrap">
              <Shield size={11} className="text-stone-500" />
              Security Key
              {keyMasked ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <Check size={9} />
                  Saved
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                  Not saved
                </span>
              )}
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={securityKey}
                onChange={(e) => setSecurityKey(e.target.value)}
                placeholder={keyMasked ? '•••••••••• saved · leave blank to keep ••••••••••' : 'Paste NMI API security key'}
                className={inputCls + ' pr-10'}
                spellCheck={false}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowKey(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600"
                aria-label={showKey ? 'Hide' : 'Show'}
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-[11px] text-stone-500 mt-1">
              NMI Merchant Portal → Settings → Security Keys. This key alone authenticates the merchant — NMI doesn&apos;t use a separate merchant id.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-stone-700 mb-1 inline-flex items-center gap-1 flex-wrap">
              <Shield size={11} className="text-stone-500" />
              Webhook Signing Key
              {secretMasked ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                  <Check size={9} />
                  Saved
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                  Not saved
                </span>
              )}
            </label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={secretMasked ? '•••••••••• saved · leave blank to keep ••••••••••' : 'NMI-generated signing key from Webhooks page'}
                className={inputCls + ' pr-10'}
                spellCheck={false}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-600"
                aria-label={showSecret ? 'Hide' : 'Show'}
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-[11px] text-stone-500 mt-1">
              Shown on the NMI Webhooks settings page after you create the endpoint below.
            </p>
          </div>

          {/* Webhook URL */}
          <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 mb-3">
            <div className="flex items-start gap-2 mb-2">
              <Info size={12} className="text-stone-500 shrink-0 mt-0.5" />
              <p className="text-[11px] font-medium text-stone-700">Webhook endpoint to register in NMI</p>
            </div>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 text-[11px] font-mono text-emerald-700 bg-white px-2 py-1.5 rounded border border-stone-200 break-all select-all">
                {url}
              </code>
              <button
                type="button"
                onClick={copyUrl}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg bg-white text-stone-700 border border-stone-300 hover:bg-stone-50 shrink-0"
              >
                {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !configured}
              title={!configured ? 'Save credentials first' : undefined}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-white text-stone-800 border border-stone-300 hover:bg-stone-50 disabled:opacity-50"
            >
              {testing ? <RubyGemMark size={14} drawing color="currentColor" /> : <Radio size={14} />}
              Test connection
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save credentials'}
            </button>
          </div>

          {lastTestResult && (
            <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg border ${
              lastTestResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
            }`}>
              <Info size={12} className={`shrink-0 mt-0.5 ${lastTestResult.ok ? 'text-emerald-600' : 'text-red-600'}`} />
              <p className={`text-xs ${lastTestResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                {lastTestResult.message}
              </p>
            </div>
          )}
          {!lastTestResult && lastTestedAt && (
            <p className="text-[11px] text-stone-400 mt-3">
              Last tested {formatRel(lastTestedAt)}.
            </p>
          )}
        </div>
      )}

      {/* Events collapsible — separate so details can be closed independently */}
      <button
        type="button"
        onClick={() => setEventsOpen(v => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-stone-50 transition-colors border-t border-stone-100"
      >
        <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
          <Bell size={14} className="text-stone-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-stone-800 font-semibold text-sm">Events to subscribe in NMI</p>
          <p className="text-stone-500 text-xs mt-0.5">
            We recommend ticking <span className="font-medium">every event</span> once. Future features (subscriptions, chargebacks) will receive data automatically.
          </p>
        </div>
        <ChevronDown size={16} className={`text-stone-400 shrink-0 transition-transform ${eventsOpen ? '' : '-rotate-90'}`} />
      </button>

      {eventsOpen && (
        <div className="px-5 pb-5 border-t border-stone-100 pt-3 space-y-3">
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
            <div className="flex items-start gap-2">
              <Info size={12} className="text-blue-600 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                <span className="font-medium">One-time setup.</span> Subscribe to all events now so tenants never have to revisit NMI Webhooks when we add subscriptions, chargebacks, or loyalty later.
                Our handler acks unknown types 200 and logs them until the feature ships.
              </p>
            </div>
          </div>

          {EVENT_GROUPS.map(group => (
            <div key={group.title} className="rounded-lg border border-stone-200 overflow-hidden">
              <div className="px-3 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2 flex-wrap">
                <p className="text-xs font-semibold text-stone-800">{group.title}</p>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                  group.live ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-stone-100 text-stone-600 border-stone-200'
                }`}>
                  {group.live ? 'Active today' : 'Queued for future'}
                </span>
              </div>
              <div className="px-3 py-2.5 bg-white">
                <p className="text-[11px] text-stone-500 mb-1.5">{group.note}</p>
                <div className="flex flex-wrap gap-1">
                  {group.events.map(ev => (
                    <code key={ev} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-stone-50 border border-stone-200 text-stone-700 select-all">
                      {ev}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function formatRel(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default NmiCredentialsCard;
